'use client'

import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog"
import { useState, useEffect } from 'react'
import { useBrands } from '@/lib/BrandsProvider'
import NoBrandSelected from '@/components/NoBrandSelected'
import confetti from 'canvas-confetti'
import { ContentTypeMetadata, useContentTypes } from '@/lib/content/contentTypes'
import ContentSetupFlow from './ContentSetupFlow'
import { getIcon } from '@/lib/content/iconMapping'
import { useAuth } from '@/lib/auth.tsx'
import { useAnalytics } from '@/lib/analytics'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { PlusCircle, AlertCircle, ArrowRight, Zap, CheckCircle, Clock, BarChart3, Search, Brain, Target, TrendingUp, Sparkles } from 'lucide-react'
import { PulseLoader } from "react-spinners"
import { cn } from "@/lib/utils"

// Define CREATION_MESSAGES here to maintain reference in createContent function
const CREATION_MESSAGES = [
    "Analyzing your campaign details...",
    "Preparing your content workspace...",
    "Gathering brand data...",
    "Finalizing your content environment...",
    "Generating content...",
    "Running QA checks...",
    "Getting ready to launch..."
];

// Define CreateContentDialog component inline to resolve import issues
interface CreateContentDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    shownMessages: string[];
    currentMessageIndex: number;
    isLoading: boolean;
}

function CreateContentDialog({
    open,
    onOpenChange,
    shownMessages,
    currentMessageIndex,
    isLoading
}: CreateContentDialogProps) {
    const [progress, setProgress] = useState(0)
    const [stepIndex, setStepIndex] = useState(0)
    const [isTransitioning, setIsTransitioning] = useState(false)

    // Rich step sequence mirroring homepage loader vibe
    const steps = [
        { text: 'Scanning inputs', loader: 'scan' },
        { text: 'Analyzing content patterns', loader: 'content' },
        { text: 'Evaluating brand voice', loader: 'voice' },
        { text: 'Optimizing experience', loader: 'ux' },
        { text: 'Processing data', loader: 'data' },
        { text: 'Generating insights', loader: 'insights' },
        { text: 'Optimizing strategy', loader: 'optimize' },
        { text: 'Building recommendations', loader: 'recommendations' },
        { text: 'Finalizing', loader: 'complete' }
    ] as const

    useEffect(() => {
        if (!open) {
            setProgress(0)
            setStepIndex(0)
            return
        }

        let elapsed = 0
        const duration = 60000 // 60s visual journey
        const tick = setInterval(() => {
            elapsed += 100
            const ratio = elapsed / duration
            const eased = ratio < 0.8 ? ratio * 100 : 80 + (ratio - 0.8) * 100
            setProgress(Math.min(95, eased))

            const newIndex = Math.floor(elapsed / 6000) % (steps.length - 1)
            setStepIndex(prev => {
                if (newIndex !== prev) {
                    setIsTransitioning(true)
                    setTimeout(() => setIsTransitioning(false), 250)
                }
                return newIndex
            })
        }, 100)

        return () => clearInterval(tick)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const getStepIcon = (type: string) => {
        switch (type) {
            case 'scan':
                return <Search className="h-6 w-6 text-white" />
            case 'content':
                return <BarChart3 className="h-6 w-6 text-white" />
            case 'voice':
                return <Target className="h-6 w-6 text-white" />
            case 'ux':
                return <TrendingUp className="h-6 w-6 text-white" />
            case 'data':
                return <Brain className="h-6 w-6 text-white" />
            case 'insights':
                return <Sparkles className="h-6 w-6 text-white" />
            case 'optimize':
                return <Zap className="h-6 w-6 text-white" />
            case 'recommendations':
                return <Target className="h-6 w-6 text-white" />
            case 'complete':
                return <CheckCircle className="h-6 w-6 text-white" />
            default:
                return <Clock className="h-6 w-6 text-white" />
        }
    }

    const getLoaderVisual = (type: string) => {
        switch (type) {
            case 'scan':
                return (
                    <div className="relative w-48 h-28 mx-auto">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-blue-50/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-sm">
                            {[...Array(10)].map((_, i) => (
                                <div key={i} className="absolute h-px bg-gradient-to-r from-transparent via-blue-500/80 to-transparent" style={{ top: `${(i + 1) * 9}%`, left: '15%', right: '15%', animation: 'scan 3s ease-in-out infinite', animationDelay: `${i * 0.08}s` }} />
                            ))}
                            <div className="absolute w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent" style={{ animation: 'scanLine 2s ease-in-out infinite' }} />
                        </div>
                    </div>
                )
            case 'content':
                return (
                    <div className="relative w-56 h-32 mx-auto">
                        <div className="bg-white/30 backdrop-blur-sm rounded-2xl border border-white/20 p-3 h-full">
                            <div className="space-y-2">
                                {[...Array(3)].map((_, card) => (
                                    <div key={card} className="bg-white/40 rounded-xl p-2">
                                        <div className="h-3 w-28 rounded-md bg-gradient-to-r from-gray-300/60 via-white/70 to-gray-300/60" style={{ backgroundSize: '200% 100%', animation: 'shimmer 2.2s ease-in-out infinite' }} />
                                        <div className="mt-2 space-y-2">
                                            {[...Array(2)].map((_, line) => (
                                                <div key={line} className="h-2 rounded-md bg-gradient-to-r from-gray-300/50 via-white/60 to-gray-300/50" style={{ backgroundSize: '200% 100%', animation: 'shimmer 2.2s ease-in-out infinite', animationDelay: `${0.12 * line}s` }} />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            case 'voice':
                return (
                    <div className="relative w-48 h-28 mx-auto flex items-center justify-center">
                        <div className="bg-gradient-to-br from-white/40 to-purple-50/60 backdrop-blur-sm rounded-2xl border border-white/30 p-4 flex items-center justify-center">
                            <div className="flex space-x-1.5">
                                {[...Array(8)].map((_, i) => (
                                    <div key={i} className="w-2 bg-gradient-to-t from-purple-600 via-indigo-500 to-blue-400 rounded-full" style={{ height: `${18 + Math.sin(Date.now() * 0.005 + i) * 22}px`, animation: `${1.5 + i * 0.1}s voice ease-in-out infinite`, animationDelay: `${i * 0.1}s` }} />
                                ))}
                            </div>
                        </div>
                    </div>
                )
            case 'ux':
                return (
                    <div className="relative w-72 h-40 mx-auto">
                        <div className="absolute inset-0 rounded-[18px] bg-gradient-to-br from-white/50 to-cyan-50/60 border border-white/40 shadow-xl backdrop-blur-sm" />
                        <div className="absolute inset-2 rounded-[14px] overflow-hidden bg-gradient-to-b from-white/70 to-sky-50/60">
                            <div className="absolute left-0 right-0" style={{ animation: 'pageScrollUX 6s ease-in-out infinite' }}>
                                <div className="px-3 pt-2 pb-4">
                                    <div className="h-5 w-24 rounded-md bg-gradient-to-r from-sky-200 to-indigo-200 mb-2" />
                                    <div className="space-y-2">
                                        <div className="h-3 rounded-md bg-sky-200/70" />
                                        <div className="h-3 rounded-md bg-sky-200/50" />
                                        <div className="h-3 rounded-md bg-sky-200/60 w-4/5" />
                                    </div>
                                </div>
                                <div className="px-3 pt-2 pb-4">
                                    <div className="grid grid-cols-3 gap-2">
                                        {[...Array(6)].map((_, i) => (
                                            <div key={i} className="h-10 rounded-xl bg-white/70 border border-white/40" />
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="absolute w-4 h-4 rounded-full bg-white shadow ring-2 ring-indigo-400/80" style={{ animation: 'cursorMoveUX 6s ease-in-out infinite' }}>
                                <span className="absolute -inset-3 rounded-full border-2 border-indigo-400/70" style={{ animation: 'tapRippleUX 6s ease-in-out infinite' }} />
                            </div>
                        </div>
                    </div>
                )
            case 'data':
                return (
                    <div className="relative w-56 h-32 mx-auto">
                        <div className="bg-white/30 backdrop-blur-sm rounded-2xl border border-white/20 p-4 h-full">
                            {[0,1,2].map((row) => (
                                <div key={row} className="relative">
                                    <div className="absolute left-3 right-3 h-1 rounded-full" style={{ top: `${10 + row * 26}px`, background: 'linear-gradient(90deg, rgba(99,102,241,0.35), rgba(59,130,246,0.55), rgba(99,102,241,0.35))' }} />
                                    {[0,1,2].map((p) => (
                                        <span key={p} className="absolute w-2.5 h-2.5 rounded-full" style={{ top: `${9 + row * 26}px`, left: '-12px', background: 'linear-gradient(135deg, #818cf8, #60a5fa)', boxShadow: '0 0 8px rgba(96, 165, 250, 0.6)', animation: 'packetMove 2.4s linear infinite', animationDelay: `${row * 0.3 + p * 0.5}s` }} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            case 'insights':
                return (
                    <div className="relative w-48 h-28 mx-auto flex items-center justify-center">
                        <div className="bg-gradient-to-br from-white/40 to-yellow-50/60 backdrop-blur-sm rounded-2xl border border-white/30 p-4 flex items-center justify-center">
                            <div className="relative">
                                <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-yellow-100 to-amber-200">
                                    <Sparkles className="w-8 h-8 text-yellow-600" />
                                </div>
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="absolute w-2.5 h-2.5 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full" style={{ left: '50%', top: '50%', transform: `rotate(${i * 60}deg) translateY(-38px)`, transformOrigin: '50% 50%', animation: 'insights 2s ease-in-out infinite', animationDelay: `${i * 0.2}s` }} />
                                ))}
                            </div>
                        </div>
                    </div>
                )
            case 'optimize':
                return (
                    <div className="relative w-48 h-28 mx-auto flex items-center justify-center">
                        <div className="bg-gradient-to-br from-white/40 to-green-50/60 backdrop-blur-sm rounded-2xl border border-white/30 p-4 flex items-center justify-center">
                            <svg className="w-32 h-20" viewBox="0 0 128 80">
                                <defs>
                                    <linearGradient id="optMini" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#10b981" />
                                        <stop offset="50%" stopColor="#059669" />
                                        <stop offset="100%" stopColor="#047857" />
                                    </linearGradient>
                                </defs>
                                <path d="M 10 70 Q 30 10 50 40 T 90 20 T 118 30" stroke="url(#optMini)" strokeWidth="4" fill="none" className="animate-pulse" />
                                <circle cx="10" cy="70" r="4" fill="url(#optMini)" style={{ animation: 'optimizeDot 3s ease-in-out infinite' }} />
                            </svg>
                        </div>
                    </div>
                )
            case 'recommendations':
                return (
                    <div className="relative w-48 h-28 mx-auto flex items-center justify-center">
                        <div className="bg-gradient-to-br from-white/40 to-indigo-50/60 backdrop-blur-sm rounded-2xl border border-white/30 p-4 flex items-center justify-center">
                            <div className="relative">
                                {[...Array(3)].map((_, ring) => (
                                    <div key={ring} className="absolute rounded-full" style={{ width: `${40 + ring * 20}px`, height: `${40 + ring * 20}px`, left: `${-ring * 10}px`, top: `${-ring * 10}px`, border: '3px solid transparent', backgroundImage: 'linear-gradient(45deg, #6366f1, #8b5cf6, #a855f7)', backgroundClip: 'padding-box', animation: `recommendationRing ${2 + ring}s linear infinite`, animationDelay: `${ring * 0.5}s` }} />
                                ))}
                                <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                    <Brain className="w-4.5 h-4.5" />
                                </div>
                            </div>
                        </div>
                    </div>
                )
            case 'complete':
                return (
                    <div className="relative w-48 h-28 mx-auto flex items-center justify-center">
                        <div className="bg-gradient-to-br from-white/40 to-green-50/60 backdrop-blur-sm rounded-2xl border border-white/30 p-4 flex items-center justify-center">
                            <div className="relative">
                                <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 border-4 border-emerald-400/70">
                                    <CheckCircle className="w-10 h-10 text-green-600" />
                                </div>
                                <div className="absolute inset-0 rounded-full border-4 border-green-300/60 animate-ping" />
                            </div>
                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-md relative overflow-hidden bg-gradient-to-br from-green-50/90 via-emerald-50/60 to-white border border-green-200/60"
                style={{ 
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-green-50/30 via-transparent to-emerald-50/20" />
                <div className="relative">
                    {/* Progress bar */}
                    <div className="absolute -top-1 left-0 right-0 h-1 bg-gradient-to-r from-emerald-100 to-green-100">
                        <div className="h-full bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 transition-all duration-300" style={{ width: `${progress}%` }} />
                    </div>
                    <DialogTitle className="text-xl font-bold bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent mb-2">
                        Creating Your Content
                    </DialogTitle>
                    <DialogDescription className="text-green-600 mb-6">
                        Please wait while we set up your new content space...
                    </DialogDescription>
                    <div className="flex flex-col items-center justify-center p-4 sm:p-6 space-y-4 sm:space-y-6">
                        {/* Step header with icon */}
                        <div className={`transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl mb-3">
                                {getStepIcon(steps[stepIndex].loader)}
                            </div>
                        </div>
                        {/* Rich animated visual */}
                        <div className={`bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-sm p-4 transition-all duration-500 ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                            {getLoaderVisual(steps[stepIndex].loader)}
                        </div>
                        <div className="space-y-3 w-full">
                            {shownMessages.length > 0 && (
                                <div className="flex items-center space-x-3 text-sm text-green-700 animate-fade-in p-3 bg-green-50/60 rounded-xl border border-green-200/40">
                                    {isLoading && currentMessageIndex === shownMessages.length - 1 ? (
                                        <div className="flex gap-1">
                                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                    ) : (
                                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-sm">
                                            <div className="w-2 h-2 rounded-full bg-white" />
                                        </div>
                                    )}
                                    <span className="font-medium">{shownMessages[currentMessageIndex]}</span>
                                </div>
                            )}
                        </div>
                        {/* Small step label */}
                        <div className="text-[12px] text-emerald-700/80">
                            {steps[stepIndex].text}
                        </div>
                    </div>
                    {/* Animations for the loader visuals */}
                    <style>{`
                        @keyframes scan { 0%,100%{opacity:.4;transform:scaleX(1)} 50%{opacity:1;transform:scaleX(1.05)} }
                        @keyframes scanLine { 0%{top:0%;opacity:1;transform:scaleY(.8)} 50%{opacity:.9;transform:scaleY(1.2)} 100%{top:100%;opacity:0;transform:scaleY(.8)} }
                        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                        @keyframes voice { 0%,100%{height:22px;opacity:.6;transform:scaleY(1)} 50%{height:64px;opacity:1;transform:scaleY(1.1)} }
                        @keyframes pageScrollUX { 0%{transform:translateY(0)} 35%{transform:translateY(0)} 60%{transform:translateY(-100%)} 100%{transform:translateY(-100%)} }
                        @keyframes cursorMoveUX { 0%{transform:translate(24px,92px)} 25%{transform:translate(160px,120px)} 50%{transform:translate(126px,32px)} 75%{transform:translate(56px,128px)} 100%{transform:translate(24px,92px)} }
                        @keyframes tapRippleUX { 0%,49%{opacity:0;transform:scale(.8)} 50%{opacity:1;transform:scale(1)} 60%{opacity:0;transform:scale(1.5)} 100%{opacity:0;transform:scale(.8)} }
                        @keyframes packetMove { 0%{transform:translateX(0) scale(1);opacity:0} 10%{opacity:1} 50%{transform:translateX(200px) scale(1.05)} 100%{transform:translateX(400px) scale(1);opacity:0} }
                        @keyframes insights { 0%{opacity:0;transform:rotate(0deg) translateY(-38px) scale(0)} 50%{opacity:1;transform:rotate(180deg) translateY(-38px) scale(1.15)} 100%{opacity:0;transform:rotate(360deg) translateY(-38px) scale(0)} }
                        @keyframes recommendationRing { 0%{transform:scale(.9) rotate(0deg);opacity:1} 50%{transform:scale(1.25) rotate(180deg);opacity:.7} 100%{transform:scale(.9) rotate(360deg);opacity:1} }
                        @keyframes optimizeDot { 0%{cx:10;transform:scale(1)} 25%{cx:50;transform:scale(1.15)} 50%{cx:90;transform:scale(1)} 75%{cx:118;transform:scale(1.15)} 100%{cx:10;transform:scale(1)} }
                    `}</style>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default function ContentTypeSelection() {
    const { session, loading: authLoading } = useAuth()
    const { trackEvent, events } = useAnalytics()
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { selectedBrand } = useBrands()
    const [creatingContent, setCreatingContent] = useState<string | null>(null)
    const [showLoadingModal, setShowLoadingModal] = useState(false)
    const [showSetupFlow, setShowSetupFlow] = useState(false)
    const [selectedContentType, setSelectedContentType] = useState<ContentTypeMetadata | null>(null)
    const [shownMessages, setShownMessages] = useState<string[]>([])
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [defaultCampaignId, setDefaultCampaignId] = useState<string | null>(null)
    const [defaultConvergenceId, setDefaultConvergenceId] = useState<string | null>(null)
    
    // Error handling
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [showErrorDialog, setShowErrorDialog] = useState(false)
    
    // Request content type dialog state
    const [showRequestDialog, setShowRequestDialog] = useState(false)
    const [requestFormData, setRequestFormData] = useState({
        contentTypeRequest: '',
        name: '',
        email: '',
        additionalDetails: ''
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    
    // Prefill user's name and email when dialog opens
    useEffect(() => {
        if (showRequestDialog && session?.user) {
            setRequestFormData(prevData => ({
                ...prevData,
                name: session.user.user_metadata?.full_name || '',
                email: session.user.email || ''
            }))
        }
    }, [showRequestDialog, session])
    
    // Use our new content types hook
    const { contentTypes: availableContentTypes, loading: contentTypesLoading } = useContentTypes()

    // Check for campaignId in location state on component mount
    useEffect(() => {
        if (location.state && location.state.campaignId) {
            setDefaultCampaignId(location.state.campaignId)

            // Track that campaign was auto-selected
            trackEvent(events.FEATURE_USED, {
                feature: 'campaign_auto_selected',
                campaign_id: location.state.campaignId
            })
        }
    }, [location.state, trackEvent, events])

    // Check for convergenceId in URL query params on component mount
    useEffect(() => {
        const convergenceId = searchParams.get('convergenceId')
        if (convergenceId) {
            setDefaultConvergenceId(convergenceId)

            // Track that convergence was auto-selected
            trackEvent(events.FEATURE_USED, {
                feature: 'convergence_auto_selected',
                convergence_id: convergenceId
            })
        }
    }, [searchParams, trackEvent, events])

    // Reset message index when loading modal opens
    useEffect(() => {
        if (showLoadingModal) {
            setCurrentMessageIndex(0)
        }
    }, [showLoadingModal])

    const launchConfetti = () => {
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
        });
    };

    const handleSelect = async (contentType: string) => {
        // Don't allow selection while auth is still loading
        if (authLoading) {
            console.log('Auth still loading, please wait...')
            return
        }
        
        const selectedType = availableContentTypes.find(type => type.id === contentType)
        if (!selectedType) return
        
        // Track content type selection
        trackEvent(events.CONTENT_TYPE_SELECTED, {
            content_type: contentType,
            content_type_name: selectedType.title,
            content_type_status: selectedType.status,
            from_campaign_id: defaultCampaignId
        })
        
        // Check if content type is available based on status
        if (selectedType.status === 'NOT_AVAILABLE') {
            // Show upgrade required message or redirect to pricing
            console.log('This content type requires a higher plan')
            return
        }
        
        if (selectedType.status === 'COMING_SOON') {
            // Show coming soon message
            console.log('This content type is coming soon')
            return
        }

        // Show the setup flow for all LIVE and BETA content types
        setSelectedContentType(selectedType)
        setShowSetupFlow(true)
    }
    
    const handleSetupComplete = ({ campaignId, convergenceId, inputs, dependencies, platform_key }: {
        campaignId?: string,
        convergenceId?: string,
        inputs: Record<string, any>,
        dependencies: Record<string, string>,
        platform_key?: string
    }) => {
        if (!selectedContentType) return

        // Create the content with all collected data
        createContent(selectedContentType.id, campaignId, convergenceId, dependencies, inputs, platform_key)
        setShowSetupFlow(false)
    }

    const createContent = async (contentType: string, campaignId?: string, convergenceId?: string, dependencies?: Record<string, string>, inputs?: Record<string, any>, platform_key?: string) => {
        setCreatingContent(contentType)
        setShowLoadingModal(true)
        setIsLoading(true)
        setShownMessages([])
        setErrorMessage(null)

        if (!session) {
            if (authLoading) {
                throw new Error('Authentication is still loading. Please wait a moment and try again.');
            } else {
                throw new Error('No session found. Please sign in and try again.');
            }
        }

        // Track content generation start
        trackEvent(events.CONTENT_GENERATION_STARTED, {
            content_type: contentType,
            campaign_id: campaignId,
            has_dependencies: !!dependencies && Object.keys(dependencies).length > 0,
            has_inputs: !!inputs && Object.keys(inputs).length > 0
        });

        const showProgressMessages = (messages: string[]) => {
            setShownMessages([])
            let messageIndex = 0
            const messageInterval = setInterval(() => {
                if (messageIndex < messages.length) {
                    setShownMessages(prev => [...prev, messages[messageIndex]])
                    setCurrentMessageIndex(messageIndex)
                    messageIndex++
                }
            }, 1500)
            return messageInterval
        }

        const startTime = Date.now();
        let messageInterval: ReturnType<typeof setInterval> | undefined;
        
        try {
            messageInterval = showProgressMessages(CREATION_MESSAGES)
            const workerUrl = import.meta.env.VITE_WORKERS_URL;
            const endpointUrl = `${workerUrl}/rainfactory/content-functions/create-content`

            const response = await fetch(endpointUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json',
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
                },
                body: JSON.stringify({
                    content_type: contentType,
                    campaign_id: campaignId,
                    convergence_id: convergenceId,
                    brand_id: selectedBrand?.id,
                    dependencies,
                    inputs,
                    platform_key: platform_key
                }),
            })

            if (!response.ok) {
                const error = await response.text();
                const errorWithStatus = new Error(error || 'Failed to create content');
                (errorWithStatus as any).status = response.status;
                throw errorWithStatus;
            }

            const { id } = await response.json()
            const duration = Date.now() - startTime;
            
            // Track successful content generation
            trackEvent(events.CONTENT_GENERATION_COMPLETED, {
                content_type: contentType,
                campaign_id: campaignId,
                content_id: id,
                duration_ms: duration
            });

            await new Promise(resolve => setTimeout(resolve, 3000))

            launchConfetti()
            navigate(`/content/${id}`)
        } catch (error) {
            const duration = Date.now() - startTime;
            
            // Track content generation error
            trackEvent(events.CONTENT_GENERATION_ERROR, {
                content_type: contentType,
                campaign_id: campaignId,
                error_message: error instanceof Error ? error.message : String(error),
                duration_ms: duration
            });
            
            console.error('Error creating content:', error)
            
            // Set error message and show error dialog
            if( (error as any)?.status === 402 ) {
                setErrorMessage('You do not have enough credits to create this content. Please upgrade your plan or add more credits.');
            } else {
                setErrorMessage('We encountered an error while creating your content. Please try again later.');
            }
            setShowLoadingModal(false)
            setShowErrorDialog(true)
        } finally {
            if (messageInterval) clearInterval(messageInterval)
            if (!errorMessage) {
                setShowLoadingModal(false)
            }
            setIsLoading(false)
            setCreatingContent(null)
            setSelectedContentType(null)
            setShowSetupFlow(false)
            setShownMessages([])
        }
    }
    
    // Handle the content type request form
    const handleRequestInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setRequestFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const submitContentTypeRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            // Track that user requested a content type
            trackEvent(events.CONTENT_TYPE_REQUESTED, {
                content_type_request: requestFormData.contentTypeRequest
            });
            
            const workerUrl = import.meta.env.VITE_WORKERS_URL;
            
            // Use the existing contact form endpoint
            const response = await fetch(`${workerUrl}/marketing-functions/submit_contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: requestFormData.name,
                    email: requestFormData.email,
                    company: selectedBrand?.brand_name || '',
                    interest: 'content-type-request',
                    message: `Content Type Request: ${requestFormData.contentTypeRequest}\n\nAdditional Details: ${requestFormData.additionalDetails}`
                })
            });
            
            if (!response.ok) {
                throw new Error('Failed to submit request');
            }
            
            // Show success message
            setIsSubmitted(true);
            
            // Reset form but preserve user name and email after 3 seconds
            setTimeout(() => {
                setIsSubmitted(false);
                setShowRequestDialog(false);
                // Preserve name and email for convenience on subsequent submissions
                setRequestFormData(prev => ({
                    contentTypeRequest: '',
                    name: prev.name, // Keep the name
                    email: prev.email, // Keep the email
                    additionalDetails: ''
                }));
            }, 3000);
            
        } catch (error) {
            console.error('Error submitting content type request:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!selectedBrand) {
        return <NoBrandSelected />
    }

    return (
        <>
            <CreateContentDialog 
                open={showLoadingModal} 
                onOpenChange={setShowLoadingModal}
                shownMessages={shownMessages}
                currentMessageIndex={currentMessageIndex}
                isLoading={isLoading}
            />

            {/* Error Dialog */}
            <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
                <DialogContent 
                    className="sm:max-w-md relative overflow-hidden bg-gradient-to-br from-red-50/90 via-orange-50/60 to-white border border-red-200/60"
                    style={{ 
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 9999
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-red-50/30 via-transparent to-orange-50/20" />
                    <div className="relative">
                        <DialogTitle className="flex items-center gap-3 text-red-700 mb-4">
                            <div className="p-2 bg-gradient-to-br from-red-100 to-orange-200 rounded-xl shadow-sm">
                                <AlertCircle className="h-6 w-6 text-red-600" />
                            </div>
                            <span className="text-xl font-bold bg-gradient-to-r from-red-700 to-orange-700 bg-clip-text text-transparent">
                                Error Creating Content
                            </span>
                        </DialogTitle>
                        <DialogDescription className="text-red-600 leading-relaxed mb-6 pl-14">
                            {errorMessage || 'An unexpected error occurred.'}
                        </DialogDescription>
                        <DialogFooter>
                            <Button 
                                onClick={() => setShowErrorDialog(false)}
                                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 shadow-sm hover:shadow-md transition-all duration-300"
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Request Content Type Dialog */}
            <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
                <DialogContent 
                    className="sm:max-w-md max-h-[90vh] overflow-y-auto relative bg-gradient-to-br from-purple-50/90 via-pink-50/60 to-white border border-purple-200/60"
                    style={{ 
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 9999
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50/30 via-transparent to-pink-50/20" />
                    <div className="relative">
                        <DialogTitle className="text-xl font-bold bg-gradient-to-r from-purple-700 to-pink-700 bg-clip-text text-transparent mb-2">
                            Request Content Type
                        </DialogTitle>
                        <DialogDescription className="text-purple-600 text-sm sm:text-base mb-6">
                            Let us know what kind of content you would like Rainfactory to generate for you.
                        </DialogDescription>
                    
                    {isSubmitted ? (
                        <div className="text-center py-6 sm:py-8">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-green-100 to-emerald-200 text-green-600 mb-4 shadow-lg">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent mb-2">
                                Request Submitted!
                            </h3>
                            <p className="text-green-600 leading-relaxed mb-4">
                                Thank you for your suggestion. Our team will review your request and consider adding this content type.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={submitContentTypeRequest} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="contentTypeRequest">What content type would you like to see?</Label>
                                <Textarea
                                    id="contentTypeRequest"
                                    name="contentTypeRequest"
                                    placeholder="E.g., Social media videos, eBooks, product descriptions, etc."
                                    value={requestFormData.contentTypeRequest}
                                    onChange={handleRequestInputChange}
                                    required
                                    className="min-h-[80px] resize-none"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="name">Your Name</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    placeholder="Your name"
                                    value={requestFormData.name}
                                    onChange={handleRequestInputChange}
                                    required
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="email">Your Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="Your email address"
                                    value={requestFormData.email}
                                    onChange={handleRequestInputChange}
                                    required
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="additionalDetails">Additional Details (Optional)</Label>
                                <Textarea
                                    id="additionalDetails"
                                    name="additionalDetails"
                                    placeholder="Any specific features or requirements for this content type?"
                                    value={requestFormData.additionalDetails}
                                    onChange={handleRequestInputChange}
                                    className="min-h-[80px] resize-none"
                                />
                            </div>
                            
                            <DialogFooter className="sm:flex-row gap-2">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={() => setShowRequestDialog(false)} 
                                    className="w-full sm:w-auto border-purple-200 text-purple-600 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300 transition-all duration-300"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit" 
                                    disabled={isSubmitting} 
                                    className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-sm hover:shadow-md transition-all duration-300"
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                    </div>
                </DialogContent>
            </Dialog>

            {selectedContentType && (
                <ContentSetupFlow
                    contentType={selectedContentType}
                    isOpen={showSetupFlow}
                    onClose={() => {
                        setShowSetupFlow(false)
                        setSelectedContentType(null)
                    }}
                    onComplete={handleSetupComplete}
                    defaultCampaignId={defaultCampaignId}
                    defaultConvergenceId={defaultConvergenceId}
                />
            )}

            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                <div className="relative container mx-auto p-6 max-w-6xl">
                    {/* Enhanced Header Section */}
                    <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm mb-8 p-8">
                        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
                        <div className="relative">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                                        <Zap className="h-8 w-8 text-blue-600" />
                                    </div>
                                    <div>
                                        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent mb-2">
                                            Create Content
                                        </h1>
                                        <p className="text-gray-600 text-lg leading-relaxed max-w-2xl">
                                            Choose the type of content you'd like to create. We recommend starting with a landing page, as some content types depend on it.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Credit Usage Bar
                            <div className="flex justify-center mt-6">
                                <div className="w-full max-w-md">
                                    <CreditUsageBar showDetails={true} />
                                </div>
                            </div> */}
                        </div>
                    </div>

                    {contentTypesLoading ? (
                        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-white rounded-2xl border border-blue-200/60 shadow-lg">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20" />
                            <div className="relative flex flex-col justify-center items-center py-16 px-8">
                                <div className="mb-6">
                                    <PulseLoader 
                                        color="#3B82F6"
                                        size={12}
                                        margin={4}
                                        speedMultiplier={0.8}
                                    />
                                </div>
                                <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent mb-2">
                                    Loading Content Types
                                </h3>
                                <p className="text-blue-600 text-center max-w-sm">
                                    Discovering available content types for your brand...
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {availableContentTypes.map((type) => {
                                const Icon = getIcon(type.iconName)
                                const isCreating = creatingContent === type.id
                                
                                return (
                                    <div
                                        key={type.id}
                                        onClick={(type.status === 'LIVE' || type.status === 'BETA') && !authLoading ? () => handleSelect(type.id) : undefined}
                                        className={cn(
                                            "group relative overflow-hidden bg-gradient-to-br from-white/90 via-blue-50/60 to-indigo-50/40 rounded-2xl border border-blue-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer p-6 min-h-[16rem]",
                                            "dark:from-gray-900/90 dark:via-blue-900/60 dark:to-indigo-900/40 dark:border-blue-800/60",
                                            creatingContent && !isCreating ? 'opacity-50 pointer-events-none' : '',
                                            type.status !== 'LIVE' && type.status !== 'BETA' ? 'opacity-80' : '',
                                            authLoading ? 'opacity-50 cursor-wait' : ''
                                        )}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20" />
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full -translate-y-16 translate-x-16" />
                                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-100/40 to-transparent rounded-full translate-y-12 -translate-x-12" />
                                        
                                        <div className="relative space-y-4">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl shadow-sm group-hover:from-blue-200 group-hover:to-indigo-300 transition-all duration-300">
                                                    <Icon className="h-6 w-6 text-blue-600 group-hover:text-blue-700 group-hover:scale-110 transition-all duration-300" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-xl font-bold bg-gradient-to-r from-blue-800 to-indigo-700 bg-clip-text text-transparent group-hover:from-blue-700 group-hover:to-indigo-600 transition-all duration-300">
                                                        {type.title}
                                                    </h3>
                                                    {type.status === 'BETA' && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 mt-1">
                                                            BETA
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <p className="text-blue-700 group-hover:text-blue-800 transition-colors duration-300 leading-relaxed flex-grow">
                                                {type.description}
                                            </p>
                                            
                                            <div className="mt-auto">
                                                <Button
                                                    variant={(type.status === 'LIVE' || type.status === 'BETA') ? "default" : "outline"}
                                                    className="w-full justify-between items-center text-sm bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm hover:shadow-md transition-all duration-300"
                                                    disabled={authLoading}
                                                >
                                                    {authLoading 
                                                        ? 'Loading...'
                                                        : type.status === 'LIVE' || type.status === 'BETA' 
                                                            ? 'Create Now' 
                                                            : type.status === 'COMING_SOON' 
                                                                ? 'Coming Soon' 
                                                                : 'Upgrade Required'}
                                                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                            
                            {/* Request Content Type Card */}
                            <div
                                onClick={!authLoading ? () => setShowRequestDialog(true) : undefined}
                                className={cn(
                                    "group relative overflow-hidden bg-gradient-to-br from-purple-50/90 via-pink-50/60 to-indigo-50/40 rounded-2xl border border-purple-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer p-6 min-h-[16rem]",
                                    "dark:from-purple-900/90 dark:via-pink-900/60 dark:to-indigo-900/40 dark:border-purple-800/60",
                                    authLoading ? 'opacity-50 cursor-wait' : ''
                                )}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-purple-50/30 via-transparent to-pink-50/20" />
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-200/30 to-transparent rounded-full -translate-y-16 translate-x-16" />
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-100/40 to-transparent rounded-full translate-y-12 -translate-x-12" />
                                
                                <div className="relative space-y-4">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-200 rounded-xl shadow-sm group-hover:from-purple-200 group-hover:to-pink-300 transition-all duration-300">
                                            <PlusCircle className="h-6 w-6 text-purple-600 group-hover:text-purple-700 group-hover:scale-110 transition-all duration-300" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-xl font-bold bg-gradient-to-r from-purple-800 to-pink-700 bg-clip-text text-transparent group-hover:from-purple-700 group-hover:to-pink-600 transition-all duration-300">
                                                Request Content Type
                                            </h3>
                                        </div>
                                    </div>
                                    
                                    <p className="text-purple-700 group-hover:text-purple-800 transition-colors duration-300 leading-relaxed flex-grow">
                                        Don't see what you need? Request a new content type and we'll consider adding it to our platform.
                                    </p>
                                    
                                    <div className="mt-auto">
                                        <Button
                                            className="w-full justify-between items-center text-sm bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-sm hover:shadow-md transition-all duration-300"
                                            disabled={authLoading}
                                        >
                                            {authLoading ? 'Loading...' : 'Request New'}
                                            <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
} 